import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { supabase } from '@/lib/supabase';
import { CheckCircle2, AlertCircle } from 'lucide-react';

export function RequestAccess() {
  const [email, setEmail] = useState('');
  const [note, setNote] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      const { error: insertError } = await supabase
        .from('access_requests')
        .insert({
          email: email.trim(),
          note: note.trim() || null,
        });

      if (insertError) throw insertError;

      setSubmitted(true);
    } catch (err: any) {
      if (err.code === '23505') {
        setError('You have already submitted an access request with this email.');
      } else {
        setError(err.message || 'Failed to submit request. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-ingesta-blue-50 to-ingesta-teal-50 flex items-center justify-center p-4">
        <Card className="w-full max-w-md mx-auto">
          <CardHeader className="text-center">
            <div className="flex justify-center mb-4">
              <CheckCircle2 className="w-16 h-16 text-green-600" />
            </div>
            <CardTitle className="text-2xl font-bold text-ingesta-blue-600">
              Request Submitted!
            </CardTitle>
            <CardDescription>
              We'll review your request shortly
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="text-center text-sm text-muted-foreground">
              <p className="mb-2">
                Thank you for your interest in Ingesta! We've received your beta access request.
              </p>
              <p className="mb-4">
                We'll review it and send a registration code to <strong>{email}</strong> if approved.
              </p>
              <p className="text-xs text-gray-500">
                This usually takes 1-2 business days.
              </p>
            </div>
            <Button
              onClick={() => navigate('/login')}
              className="w-full"
              variant="outline"
            >
              Back to Login
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-ingesta-blue-50 to-ingesta-teal-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-md mx-auto">
        <CardHeader className="text-center">
          <CardTitle className="text-2xl font-bold text-ingesta-blue-600">
            Request Beta Access
          </CardTitle>
          <CardDescription>
            Join the Ingesta private beta
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
              <div className="text-sm text-blue-800">
                <p>
                  Tell us a bit about yourself and why you're interested in Ingesta.
                  We review all requests and send access codes to approved applicants.
                </p>
              </div>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <label htmlFor="email" className="text-sm font-medium">
                Email Address *
              </label>
              <Input
                id="email"
                type="email"
                placeholder="your.email@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoFocus
              />
            </div>

            <div className="space-y-2">
              <label htmlFor="note" className="text-sm font-medium">
                Tell us about your use case <span className="text-gray-500">(optional)</span>
              </label>
              <Textarea
                id="note"
                placeholder="e.g., I'm a product manager at a tech company managing 5 projects..."
                value={note}
                onChange={(e) => setNote(e.target.value)}
                rows={4}
                className="resize-none"
              />
              <p className="text-xs text-gray-500">
                Help us understand how you plan to use Ingesta
              </p>
            </div>

            {error && (
              <div className="text-red-600 text-sm p-3 bg-red-50 border border-red-200 rounded">
                {error}
              </div>
            )}

            <Button
              type="submit"
              className="w-full"
              disabled={isLoading}
            >
              {isLoading ? 'Submitting...' : 'Request Access'}
            </Button>
          </form>

          <div className="mt-6 space-y-2 text-center">
            <p className="text-sm text-muted-foreground">
              Already have a code?{' '}
              <button
                onClick={() => navigate('/signup')}
                className="text-ingesta-blue-600 hover:underline"
              >
                Sign up
              </button>
            </p>
            <p className="text-sm text-muted-foreground">
              Already have an account?{' '}
              <button
                onClick={() => navigate('/login')}
                className="text-ingesta-blue-600 hover:underline"
              >
                Sign in
              </button>
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
